import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumb } from "@/components/Breadcrumb";
import { StructuredData } from "@/components/StructuredData";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import faqImage from "@/assets/faq-hero.jpg";

const FAQ = () => {
  const breadcrumbs = [
    { label: "FAQ", href: "/faq" }
  ];

  const faqs = [
    {
      question: "What is a random number generator?",
      answer: "A random number generator (RNG) is a tool that produces unpredictable numbers within a specified range. Our random number generator uses cryptographically secure algorithms to ensure true randomness, making it perfect for games, lotteries, raffles, statistical sampling, and security applications. Unlike basic random number generators, we use the Web Crypto API for genuine unpredictability."
    },
    {
      question: "How does the random number generator work?",
      answer: "Our random number generator uses cryptographically secure randomness through the Web Crypto API (crypto.getRandomValues()). This ensures truly random and unpredictable results that are suitable for games, drawings, and statistical applications. The algorithm generates numbers that pass rigorous statistical tests for randomness."
    },
    {
      question: "Is the number generation truly random?",
      answer: "Yes! We use cryptographically secure pseudo-random number generation (CSPRNG) which produces high-quality randomness. This is the same technology used for security-critical applications like encryption and is far superior to basic JavaScript Math.random()."
    },
    {
      question: "Can I use this for lottery or prize drawings?",
      answer: "Absolutely! Our random number generator is perfect for fair lottery drawings, prize selections, and contests. The cryptographic security ensures unbiased results that can't be predicted or manipulated. Many organizations use our random number generator for official drawings and giveaways."
    },
    {
      question: "What makes this random number generator better than others?",
      answer: "Our random number generator uses cryptographically secure algorithms (Web Crypto API) rather than predictable pseudo-random methods. This means truly unpredictable results suitable for security applications. Plus, it's completely free, unlimited, and works on all devices."
    },
    {
      question: "Can I generate lottery numbers with this random number generator?",
      answer: "Yes! Our random number generator is ideal for generating lottery numbers for any lottery game. Simply set your number range (e.g., 1-69 for Powerball) and generate as many random numbers as you need for your lottery ticket."
    },
    {
      question: "Is there a limit to how many numbers I can generate?",
      answer: "There are no limits on the number of random numbers you can generate. You can use our tools as many times as you need, completely free of charge."
    },
    {
      question: "Do you store my generated numbers?",
      answer: "For registered users, we track usage statistics to provide insights in your dashboard. However, the actual random numbers and values generated are not permanently stored. Your privacy is important to us."
    },
    {
      question: "Can I generate negative numbers?",
      answer: "Yes! Our number generator supports any range you specify, including negative numbers. Simply set your minimum value to a negative number."
    },
    {
      question: "What's the difference between coin flip and dice roller?",
      answer: "The coin flip simulates a two-sided coin toss (heads or tails), while the dice roller simulates rolling standard six-sided dice. Both use the same cryptographically secure random number generation."
    },
    {
      question: "Do I need to create an account to use the tools?",
      answer: "No account is required! You can use all our random generation tools completely free without signing up. Creating an account is optional and provides additional features like statistics tracking."
    },
    {
      question: "Are the results biased in any way?",
      answer: "No. Our generator produces uniformly distributed random numbers with no bias. Each number in your specified range has an equal probability of being selected."
    },
    {
      question: "Can I use this on mobile devices?",
      answer: "Yes! Our website is fully responsive and works perfectly on smartphones, tablets, and desktop computers. All features are available across all devices."
    },
    {
      question: "Is this service really free?",
      answer: "Yes, RandomGen is completely free to use with no hidden fees, subscriptions, or premium tiers. We believe everyone should have access to quality random number generation tools."
    },
    {
      question: "How accurate is the randomness?",
      answer: "The Web Crypto API we use passes rigorous statistical tests for randomness and is approved for cryptographic use. This means the randomness quality is excellent and suitable for any application requiring unpredictable results."
    }
  ];

  return (
    <Layout>
      <SEO 
        title="FAQ - Random Number Generator Questions Answered"
        description="Frequently asked questions about our cryptographically secure random number generator. Learn about RNG quality, lottery drawings, security, and how our tools work."
        canonical="https://numbers-generator.com/faq"
        keywords="random number generator faq, RNG questions, lottery generator help, random generator guide"
      />
      <StructuredData type="FAQPage" faqs={faqs} />
      <StructuredData 
        type="BreadcrumbList"
        breadcrumbs={[
          { name: "Home", url: "https://numbers-generator.com/" },
          { name: "FAQ", url: "https://numbers-generator.com/faq" }
        ]}
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Breadcrumb items={breadcrumbs} />
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
            RandomGen FAQ - Random Number Generator Questions
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about our free random number generator, cryptographically secure RNG, 
            lottery number generation, and random tools
          </p>
        </header>

        <Card className="p-6 md:p-8 mb-12">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>

        <div className="mb-12 text-center">
          <Card className="p-6 bg-accent/50">
            <h2 className="text-xl font-semibold mb-2">Still have questions?</h2>
            <p className="text-muted-foreground mb-4">
              We're here to help! Reach out to us anytime.
            </p>
            <a href="/contact" className="text-primary hover:underline font-medium">
              Contact Support →
            </a>
          </Card>
        </div>

        {/* FAQ Hero Image at Bottom */}
        <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={faqImage} 
            alt="Frequently Asked Questions visualization with illuminated question mark and floating text bubbles - comprehensive answers about random number generator, cryptographic security, and lottery tools"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default FAQ;
